import { useTranslation } from "next-i18next";
import { getI18nProps } from '@/lib/getStatic';
import i18nextConfig from '../../../../next-i18next.config';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { ArticleJsonLd } from '@/components/SEO';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getPost, getPostBySlug, getPosts } from '@/lib/blog';
import { GetStaticPropsContext } from 'next';

export default function BlogPost({ post }: { post: any }) {
  const router = useRouter();
  const { locale } = router.query;
  const currentLocale = (locale as string) || 'en';
  const { t } = useTranslation('common');

  if (!post) return null;

  const postUrl = `https://www.penetrify.cloud/${currentLocale}/blog/${post.slug}-${post.id}`;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      <SEO
        title={`${post.title} | Penetrify Blog`}
        description={post.summary}
        ogType="article"
        ogImage={post.image}
        article={{ publishedTime: post.date_published }}
      />
      <ArticleJsonLd
        title={post.title}
        description={post.summary}
        image={post.image}
        datePublished={post.date_published}
        url={postUrl}
      />

      <Navbar />

      <main style={{ paddingTop: '60px' }}>
        {/* Article header */}
        <section style={{ padding: '72px 0 48px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 24px' }}>
            {/* Back link */}
            <Link
              href={`/${currentLocale}/blog`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '40px', transition: 'color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <svg style={{ width: '12px', height: '12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              {t('common:blog.back_to_blog')}
            </Link>

            {/* Date */}
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '20px' }}>
              {new Date(post.date_published).toLocaleDateString(currentLocale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>

            {/* Title */}
            <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, color: 'var(--text)' }}>
              {post.title}
            </h1>
          </div>
        </section>

        {/* Article body */}
        <article style={{ maxWidth: '760px', margin: '0 auto', padding: '64px 24px 100px' }}>
          <div
            className="prose-penetrify"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Footer divider */}
          <div style={{ marginTop: '80px', paddingTop: '40px', borderTop: '1px solid var(--border)' }}>
            <Link
              href={`/${currentLocale}/blog`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '1.5px', transition: 'color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <svg style={{ width: '12px', height: '12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              {t('common:blog.back_to_blog')}
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export async function getStaticPaths() {
  const locales = i18nextConfig.i18n.locales;
  const paths = [];

  for (const locale of locales) {
    const posts = getPosts(locale);
    for (const post of posts) {
      paths.push({ params: { locale, slugId: post.slug } });
    }
  }

  return { paths, fallback: false };
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const locale = ctx.params?.locale as string;
  const slugId = ctx.params?.slugId as string;

  const parts = slugId.split('-');
  const lastPart = parts[parts.length - 1];
  const post = /^\d+$/.test(lastPart)
    ? getPost(lastPart, locale)
    : getPostBySlug(slugId, locale);

  return {
    props: {
      ...(await getI18nProps(ctx, ['common'])),
      post,
    }
  };
}
